// 🔩 Base
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

// 🌍 i18n
import { LocalizedMessage } from 'intl/LocalizedComponents';

// 📏 Types
import PropTypes from 'prop-types';
import {
  cpmParkingCategoryDataShape, cpmPriceGridDataShape, errorShape,
} from 'constants/PropTypesShapes';

// 🔧 Utils
import _get from 'lodash/get';
import { buildFormErrors, cpmParkingCategoryPriceGridConstraints } from 'utils/Forms';
import { sanitizeFloatRounded } from 'utils/Tools';

// 🫔 HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';

// 🧱 Components
import Card from 'components/core/Card';
import Typography from 'components/core/Typography';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import NumberInputField from 'components/inputFields/NumberInputField';
import ResourcesSelectFieldWithCreateButton from 'components/inputFields/ResourcesSelectFieldWithCreateButton';
import PriceGridInfoButton from 'cpmCarparks/assets/priceGrids/PriceGridInfoButton';
import ItemsSelectField from 'components/inputFields/ItemsSelectField';
import { PRICE_GRID_CALCULATION_MODES } from 'constants/App';

// 🗿 Models
import mPriceGrid from 'models/cpm/priceGrid';
import { serializePercentage } from 'models/cpm/schemas';

const ParkingCategoryAddPriceGridForm = ({
  fields: {
    priceGridId,
    discount,
    priceGridCalculationMode,
  },
  priceGrids,
  priceGridAdd,
  errors,
  parkingCategory,
  priceGridsLoading,
  onFieldsChange,
  onErrorsChange,
  onAddPriceGrid,
  onValidate,
  onRef,
}) => {
  const addedPlanningId = React.useRef(null);

  const handleValidate = () => {
    const clientFormErrors = buildFormErrors({ priceGridId, discount, priceGridCalculationMode }, cpmParkingCategoryPriceGridConstraints);
    onErrorsChange(clientFormErrors);

    // ❌ Early return if client error
    if (clientFormErrors) return false;

    onValidate({
      priceGridId,
      discount: serializePercentage(discount),
      calculationMode: priceGridCalculationMode,
    }, parkingCategory);
    return true;
  };

  React.useEffect(() => {
    onRef({ handleValidate });
  }, [handleValidate]);

  React.useEffect(() => {
    if (priceGridAdd.added) {
      addedPlanningId.current = priceGridAdd.result.id;
    }
  }, [priceGridAdd.added]);

  React.useEffect(() => {
    if (!priceGridsLoading) {
      onFieldsChange({
        priceGridId: addedPlanningId.current,
      });
    }
  }, [priceGridsLoading]);

  /* Price grid */
  /* ========== */

  const PriceGridTitle = (
    <Typography type="subtitle2">
      <LocalizedMessage id="field.priceGrid" />
    </Typography>
  );

  const HelperPriceGrid = <FormHelper elements={[{ content: <LocalizedMessage id="help.parkingCategoriesPriceGrid.priceGrid" /> }]} />;

  const InfoButton = (!priceGridsLoading && priceGridId) ? (
    <div className="ml-1 mb-2">
      <PriceGridInfoButton priceGridId={priceGridId} />
    </div>
  ) : null;

  const DiscountInput = (
    <NumberInputField
      label={<LocalizedMessage id="field.discountApplied" />}
      errorText={_get(errors, 'discount')}
      min={0}
      max={100}
      value={discount}
      required
      sanitizer={sanitizeFloatRounded(2)}
      inputMaxWidth={70}
      adornments={{ start: '%' }}
      onChange={(value) => onFieldsChange({ discount: value })}
    />
  );

  /* Discount */
  /* ======== */
  const HelperDiscount = <FormHelper elements={[{ content: <LocalizedMessage id="help.parkingCategoriesPriceGrid.discount" /> }]} />;

  /* ================= */
  /*        UI         */
  /* ================= */

  return (
    <FormDialogTemplate errors={errors}>
      {/* 🪙 Price grid */}
      <div className="d-flex mb-3">
        {HelperPriceGrid}
        <Card title={PriceGridTitle} style={{ flexGrow: 1 }}>
          <ResourcesSelectFieldWithCreateButton
            loading={priceGridsLoading}
            infoButton={InfoButton}
            selectFieldProps={{
              errorText: _get(errors, 'priceGridId'),
              labelId: 'field.priceGrid',
              value: priceGridId,
              required: true,
              disabled: priceGrids && priceGrids.length === 0,
              resources: mPriceGrid.sortElements(priceGrids),
              withNullItem: true,
              displayKey: 'name',
              onChange: (value) => onFieldsChange({ priceGridId: value }),
            }}
            onAddResource={onAddPriceGrid}
          />
        </Card>
      </div>

      {/* 📉 Discount */}
      <div className="d-flex">
        {HelperDiscount}
        <Card style={{ flexGrow: 1 }}>
          {DiscountInput}
        </Card>
      </div>

      {/* 🧮 Calculation Mode */}
      <div className="d-flex mt-3">
        <FormHelper elements={[{ content: <LocalizedMessage id="help.parkingCategoriesPriceGrid.calculationMode" /> }]} />
        <Card style={{ flexGrow: 1 }}>
          <ItemsSelectField
            labelId="field.priceGridCalculationMode"
            errorText={_get(errors, 'priceGridCalculationMode')}
            value={priceGridCalculationMode}
            items={PRICE_GRID_CALCULATION_MODES}
            required
            onChange={(value) => onFieldsChange({ priceGridCalculationMode: value })}
          />
        </Card>
      </div>

    </FormDialogTemplate>
  );
};

ParkingCategoryAddPriceGridForm.propTypes = {
  parkingCategory: cpmParkingCategoryDataShape.isRequired,
  priceGrids: PropTypes.arrayOf(cpmPriceGridDataShape),
  priceGridsLoading: PropTypes.bool.isRequired,
  priceGridAdd: PropTypes.shape({
    added: PropTypes.bool.isRequired,
    result: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onAddPriceGrid: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};

ParkingCategoryAddPriceGridForm.defaultProps = {
  priceGrids: null,
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  priceGridId: {
    defaultValue: null,
  },
  discount: {
    defaultValue: 0,
  },
  priceGridCalculationMode: {
    defaultValue: 'PRORATED',
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  operationError: ownProps.operationError,
});

const mapStateToProps = (state) => ({
  operationError: state.cpmCarparkParkingCategory.addPriceGrid.error,
  priceGrids: state.cpmCarparkPriceGrid.list.elements,
  priceGridsLoading: state.cpmCarparkPriceGrid.list.loading,
  priceGridAdd: state.cpmCarparkPriceGrid.add,
});

module.exports = compose(
  connect(
    mapStateToProps,
  ),
  withFormManagement(
    mapFieldsToProps,
    mapOwnPropsToResources,
    false,
  ),
  withStateReinit(['addCarparkPriceGridParkingCategory']),
)(ParkingCategoryAddPriceGridForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/parkingCategories/ParkingCategoryAddPriceGridForm.js